<template>
  <el-form ref="formRef" :model="creativeForm" :rules="getCreativeFormRules.call(creativeForm)" label-width="0">
    <el-form-item prop="creativeName">
      <div class="upload-row">
        <div class="creative-name">
          <span>创意组名称：</span>
          <el-input v-model="creativeForm.creative_name" @blur="onCreativeNameInputBlur" placeholder="创意组名称"></el-input>
        </div>
        <template v-if="isWxgamePlayable">
          <el-button type="primary" @click="emit('show-material-selector', 'wxgamePlayable')"> 选择试玩结束页 </el-button>
          <span>&ensp;({{ len }}/1)</span>
        </template>
        <template v-else-if="isCarouselThreeImage">
          <el-button type="primary" :disabled="len >= 3" @click="emit('show-material-selector', 'carouselImage')"> 选择轮播三图 </el-button>
          <span>&ensp;({{ len }}/3)</span>
          <el-button type="text" @click="clearAllSelectedMaterials">&emsp;&emsp;清空</el-button>
        </template>
        <template v-else-if="isCarouselThreeVideo">
          <el-button type="primary" :disabled="len >= 3" @click="emit('show-material-selector', 'carouselVideo')"> 选择轮播三视频 </el-button>
          <span>&ensp;({{ len }}/3)</span>
          <el-button type="text" @click="clearAllSelectedMaterials">&emsp;&emsp;清空</el-button>
        </template>
        <template v-else>
          <el-button type="primary" @click="emit('show-material-selector')"> 选择视频/图片 </el-button>
          <el-button type="primary" @click="emit('show-material-selector', 0)"> 选择组图 </el-button>

          <el-dropdown @command="showProceduralImageSelector">
            <el-button style="margin-left: 10px; padding: 10px 15px" type="primary">
              上传组图
              <i class="el-icon-arrow-down el-icon--right"></i>
            </el-button>

            <el-dropdown-menu v-if="isSearchAd" slot="dropdown">
              <el-dropdown-item :command="MATERIAL_IMAGE_GROUP_TYPE.FOUR_IMAGE">1:1四图</el-dropdown-item>
            </el-dropdown-menu>

            <el-dropdown-menu v-else slot="dropdown">
              <el-dropdown-item :command="MATERIAL_IMAGE_GROUP_TYPE.THREE_IMAGE">1:1三图</el-dropdown-item>
              <el-dropdown-item :command="MATERIAL_IMAGE_GROUP_TYPE.FOUR_IMAGE">1:1四图</el-dropdown-item>
              <el-dropdown-item :command="MATERIAL_IMAGE_GROUP_TYPE.SIX_IMAGE">1:1六图</el-dropdown-item>
              <el-dropdown-item :command="MATERIAL_IMAGE_GROUP_TYPE.NINE_IMAGE">1:1九图</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <span>&ensp;({{ len }}/15)</span>
          <el-button type="text" @click="clearAllSelectedMaterials">&emsp;&emsp;清空</el-button>
        </template>
      </div>
    </el-form-item>
    <p v-if="isSearchAd" style="color: red; margin-bottom: 20px">
      腾讯搜索计划素材选择请注意版位，如果未包含微信搜一搜，不能选择9:16的竖版素材
    </p>

    <el-form-item>
      <div class="selected-materials">
        <div class="selected-videos">
          <VideoMaterial
            v-for="(item, index) in creativeForm.selectedMaterials.video"
            :key="item.id"
            v-bind="{ ...item, index }"
            @remove="onMaterialRemove('video', index)"
            @upload="onVideoReplace(index, $event)"
          >
          </VideoMaterial>
        </div>

        <div class="selected-images" v-if="creativeForm.selectedMaterials.image.length">
          <ImageMaterial
            v-for="(item, index) in creativeForm.selectedMaterials.image"
            :key="item.id"
            v-bind="{ ...item, index }"
            @remove="onMaterialRemove('image', index)"
            @upload="onImageReplace(index, $event)"
          >
          </ImageMaterial>
        </div>

        <div class="selected-images">
          <ImageMaterial
            v-for="(item, index) in creativeForm.selectedMaterials.image_list"
            :key="item.id"
            v-bind="{ ...item, index, showBtn: false }"
            @remove="onMaterialRemove('image_list', index)"
          >
            <template #material-type>
              <el-tag class="badge">组图{{ index + 1 }}</el-tag>
            </template>
          </ImageMaterial>
        </div>
      </div>
    </el-form-item>
  </el-form>
</template>

<script setup>
import { ref, toRef, unref, watch } from 'vue'
import { MATERIAL_TYPE_ID, MATERIAL_TYPE } from '@/components/material-selector/consts.js'
import VideoMaterial from '@/views/create/gdt/v3/components/creative/video.vue'
import ImageMaterial from '@/views/create/gdt/v3/components/creative/image.vue'
import ImageGroupMaterial from '@/views/create/gdt/v3/components/creative/image-group.vue'
import { MATERIAL_TYPE_SIZES_V3, MATERIAL_IMAGE_GROUP_TYPE } from '@/views/create/gdt/model/creative-const.js'
import { getCreativeFormRules } from '../../rules/index.js'

// eslint-disable-next-line
const props = defineProps({
  form: Object,
  len: Number,
  isSearchAd: Boolean,
  isWxgamePlayable: Boolean,
  isCarouselThreeImage: Boolean,
  isCarouselThreeVideo: Boolean
})
// eslint-disable-next-line
const emit = defineEmits(['update:form', 'show-material-selector', 'show-image-audio-add-modal'])

const formRef = ref(null)

// 本组件的form修改不能响应式更改外部，而是通过emit('update:form')来修改，或外部调用setForm方法更新内部
// 之所以这么处理，是创意组名称input输入时，输入一下会失去焦点
const creativeForm = ref({ ...props.form })

/**
 * @description: 删除已选素材
 * @param {*} materialType
 * @param {*} index
 */
function onMaterialRemove(materialType, index) {
  const newForm = unref(creativeForm)
  newForm.selectedMaterials[materialType] = newForm.selectedMaterials[materialType].filter((_, _index) => _index !== index)
  emit('update:form', newForm)
}

function onVideoReplace(index, e) {
  emit('replace', index, e)
}
function onImageReplace(index, e) {
  emit('replace', index, e)
}

/**
 * @description: 组图单个替换
 * @param {*} e   assetType: 'image', groupIndex: 组图内图片索引
 * @param {*} asset  需要替换的素材
 * @param {*} index 组图索引
 */
function onMaterialImageGroupReplace({ assetType, groupIndex }, asset, index) {
  emit('replace', index, assetType, { asset, groupIndex })
}

function onCreativeNameInputBlur() {
  emit('update:form', creativeForm.value)
}

function toggleMaterialTag(e) {
  creativeForm.value.materialType = e
  emit('update:form', creativeForm.value)
}

function showProceduralImageSelector(cmd) {
  emit('show-material-selector', cmd)
}

function clearAllSelectedMaterials() {
  const newForm = unref(creativeForm)
  newForm.selectedMaterials = {
    video: [],
    image: [],
    image_list: []
  }
  emit('update:form', newForm)
}

/**
 * @description: 删除单个已选素材hotfix/
 * @param {*} imgItem
 */
function onSelectedItemRemove(index, type) {
  const newForm = unref(creativeForm)
  newForm.selectedMaterials[type] = newForm.selectedMaterials[type].filter((_, _index) => _index !== index)
  emit('update:form', newForm)
}

// eslint-disable-next-line
defineExpose({
  setForm: (form) => {
    creativeForm.value = form
  },
  getForm: () => unref(creativeForm),
  validate: () => formRef.value.validate()
})
</script>

<style lang="scss" scoped>
.material-types {
  .el-tag {
    margin-right: 10px;
    cursor: pointer;
  }
}

.upload-row {
  margin-bottom: 10px;
  display: flex;

  .creative-name {
    display: flex;
    margin-right: 10px;

    span {
      width: 130px;
    }
  }
}

.selected-materials {
  .selected-images,
  .selected-videos {
    margin-bottom: 20px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    position: relative;
  }
}
</style>
